import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import organisationService from '../../services/organisationService';
import { TRACKING_CATEGORIES } from '../../constants/trackingCategories';
import '../../pages/monitoring/Monitoring.css';
import './OrgOnboardingModal.css';

/**
 * Shown once to a new organisation (see DashboardLayout.js) so the dashboard
 * and future projects are tailored to what it actually needs, instead of
 * showing every compliance category by default. Also reachable later from
 * Settings > Compliance Tracking to change the choice.
 */
const OrgOnboardingModal = ({ organisation, onComplete, onDismiss, allowDismiss = false }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  // Set once completeOnboarding succeeds - holds the modal open on a final
  // "what's next" step (rather than closing immediately) so a new org
  // actually finds out it still needs to create a project and configure
  // parameters, instead of landing on an empty dashboard with no direction.
  const [savedOrganisation, setSavedOrganisation] = useState(null);
  // Deliberately blank rather than pre-checked - an org should actively
  // choose what it tracks rather than have to notice and uncheck defaults
  // it doesn't actually want.
  const [trackingCategories, setTrackingCategories] = useState(organisation?.trackingCategories || {});
  const [customCategories, setCustomCategories] = useState(organisation?.customCategories || []);
  const [customCategoryInput, setCustomCategoryInput] = useState('');
  // null = not yet chosen - deliberately no default pick, same reasoning as
  // the blank category list above.
  const [usesEiaCertificates, setUsesEiaCertificates] = useState(
    organisation?.usesEiaCertificates ?? null
  );
  // null = not yet chosen, same reasoning as usesEiaCertificates above.
  const [hasWorkersOnSite, setHasWorkersOnSite] = useState(
    organisation?.hasWorkersOnSite ?? null
  );
  const [saving, setSaving] = useState(false);

  const toggleCategory = (id) => {
    setTrackingCategories((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const addCustomCategory = () => {
    const value = customCategoryInput.trim();
    if (!value || customCategories.includes(value)) return;
    setCustomCategories((prev) => [...prev, value]);
    setCustomCategoryInput('');
  };

  const removeCustomCategory = (value) => {
    setCustomCategories((prev) => prev.filter((c) => c !== value));
  };

  const selectedCount = Object.values(trackingCategories).filter(Boolean).length + customCategories.length;

  const handleSubmit = async () => {
    try {
      setSaving(true);
      const { organisation: updated } = await organisationService.completeOnboarding({
        trackingCategories,
        customCategories,
        usesEiaCertificates,
        hasWorkersOnSite,
      });
      setSavedOrganisation(updated);
      setStep(4);
    } catch (error) {
      console.error('Failed to save onboarding preferences:', error);
      toast.error(error.response?.data?.error || 'Failed to save your preferences');
    } finally {
      setSaving(false);
    }
  };

  // The org record is already saved at this point (step 4) - these just
  // close the modal (via onComplete, which updates AuthContext so it
  // doesn't reopen) and optionally jump straight to the next action.
  const finishAndGo = (path, state) => {
    onComplete(savedOrganisation);
    if (path) navigate(path, state ? { state } : undefined);
  };

  return (
    <div className="modal-overlay onboarding-overlay">
      <div className="monitoring-modal onboarding-modal">
        <div className="modal-header">
          <h2>
            <i className="fas fa-compass"></i>{' '}
            {step === 1 ? 'What do you need to track?' : step === 2 ? 'One more thing' : step === 3 ? 'Last question' : "You're all set"}
          </h2>
          {allowDismiss && step !== 4 && (
            <button className="close-btn" onClick={onDismiss}>
              <i className="fas fa-times"></i>
            </button>
          )}
        </div>

        <div className="modal-content">
          {step === 1 ? (
            <>
              <p className="onboarding-subtitle">
                Pick the compliance categories your organisation actually needs to monitor. We'll tailor your
                dashboard shortcuts to just these - you can change this anytime from Settings.
              </p>
              <div className="onboarding-categories-grid">
                {TRACKING_CATEGORIES.map((category) => (
                  <div
                    key={category.id}
                    className={`onboarding-category-card ${trackingCategories[category.id] ? 'selected' : ''}`}
                    onClick={() => toggleCategory(category.id)}
                  >
                    <div className="onboarding-category-checkbox">
                      <input
                        type="checkbox"
                        checked={!!trackingCategories[category.id]}
                        onChange={() => toggleCategory(category.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <span className="checkmark"></span>
                    </div>
                    <div className="onboarding-category-icon" style={{ backgroundColor: `${category.color}15`, color: category.color }}>
                      <i className={`fas ${category.icon}`}></i>
                    </div>
                    <div className="onboarding-category-label">{category.label}</div>
                  </div>
                ))}
              </div>
              {selectedCount === 0 && (
                <p className="onboarding-hint warning">Select at least one category to continue.</p>
              )}

              <div className="onboarding-custom-categories">
                <label>Don't see something you track? Add it</label>
                <div className="onboarding-custom-input-row">
                  <input
                    type="text"
                    value={customCategoryInput}
                    onChange={(e) => setCustomCategoryInput(e.target.value)}
                    placeholder="e.g. Supply Chain Ethics, Data Privacy"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addCustomCategory();
                      }
                    }}
                  />
                  <button type="button" className="btn-secondary-outline" onClick={addCustomCategory}>
                    <i className="fas fa-plus"></i> Add
                  </button>
                </div>
                {customCategories.length > 0 && (
                  <div className="onboarding-custom-tags">
                    {customCategories.map((category) => (
                      <span key={category} className="onboarding-custom-tag">
                        <i className="fas fa-tag"></i> {category}
                        <button type="button" onClick={() => removeCustomCategory(category)}>
                          <i className="fas fa-times"></i>
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </>
          ) : step === 2 ? (
            <>
              <p className="onboarding-subtitle">
                Do you have a formal EIA (Environmental Impact Assessment) certificate you need to register and
                track conditions for?
              </p>
              <div className="onboarding-yes-no">
                <button
                  type="button"
                  className={`onboarding-choice ${usesEiaCertificates === true ? 'selected' : ''}`}
                  onClick={() => setUsesEiaCertificates(true)}
                >
                  <i className="fas fa-file-contract"></i>
                  <div>
                    <strong>Yes</strong>
                    <p>Show EIA Certificates & Conditions in the dashboard and sidebar</p>
                  </div>
                </button>
                <button
                  type="button"
                  className={`onboarding-choice ${usesEiaCertificates === false ? 'selected' : ''}`}
                  onClick={() => setUsesEiaCertificates(false)}
                >
                  <i className="fas fa-clipboard-check"></i>
                  <div>
                    <strong>No</strong>
                    <p>Track compliance with Inspections & Findings instead - hide EIA shortcuts for now</p>
                  </div>
                </button>
              </div>
              {usesEiaCertificates === null && (
                <p className="onboarding-hint warning">Choose Yes or No to continue.</p>
              )}
            </>
          ) : step === 3 ? (
            <>
              <p className="onboarding-subtitle">
                Do you have workers on site to track? Some organisations (e.g. NGOs doing stakeholder or GBV
                work) don't - if that's you, we'll hide worker counts across the app.
              </p>
              <div className="onboarding-yes-no">
                <button
                  type="button"
                  className={`onboarding-choice ${hasWorkersOnSite === true ? 'selected' : ''}`}
                  onClick={() => setHasWorkersOnSite(true)}
                >
                  <i className="fas fa-hard-hat"></i>
                  <div>
                    <strong>Yes</strong>
                    <p>Track worker counts per project</p>
                  </div>
                </button>
                <button
                  type="button"
                  className={`onboarding-choice ${hasWorkersOnSite === false ? 'selected' : ''}`}
                  onClick={() => setHasWorkersOnSite(false)}
                >
                  <i className="fas fa-clipboard-check"></i>
                  <div>
                    <strong>No</strong>
                    <p>Hide worker counts - not applicable to us</p>
                  </div>
                </button>
              </div>
              {hasWorkersOnSite === null && (
                <p className="onboarding-hint warning">Choose Yes or No to continue.</p>
              )}
            </>
          ) : (
            <>
              <p className="onboarding-subtitle">
                Your preferences are saved. Two things to do next before there's anything to see on your dashboard:
              </p>
              <div className="onboarding-next-steps">
                <div className="onboarding-next-step">
                  <div className="onboarding-next-step-icon"><i className="fas fa-folder-plus"></i></div>
                  <div className="onboarding-next-step-content">
                    <strong>1. Create a project</strong>
                    <p>Add each site or engagement you're tracking, and pick which of your selected categories apply to it.</p>
                  </div>
                  <button type="button" className="btn-secondary-outline" onClick={() => finishAndGo('/projects', { openCreateModal: true })}>
                    Create a Project
                  </button>
                </div>
                <div className="onboarding-next-step">
                  <div className="onboarding-next-step-icon"><i className="fas fa-sliders-h"></i></div>
                  <div className="onboarding-next-step-content">
                    <strong>2. Configure monitoring parameters</strong>
                    <p>Define what actually gets checked for each category (e.g. Noise Level, PPE availability) - this becomes the checklist used during Inspections.</p>
                  </div>
                  <button type="button" className="btn-secondary-outline" onClick={() => finishAndGo('/monitoring/parameters/new')}>
                    Configure a Parameter
                  </button>
                </div>
                {savedOrganisation?.usesEiaCertificates && (
                  <div className="onboarding-next-step">
                    <div className="onboarding-next-step-icon"><i className="fas fa-file-contract"></i></div>
                    <div className="onboarding-next-step-content">
                      <strong>3. Register your EIA certificate</strong>
                      <p>Once you have a project, register its certificate and define the conditions you need to monitor.</p>
                    </div>
                    <button type="button" className="btn-secondary-outline" onClick={() => finishAndGo('/eia/certificates')}>
                      Go to EIA Certificates
                    </button>
                  </div>
                )}
              </div>
              <p className="onboarding-hint">
                You can revisit any of this later from the sidebar, or change what you track anytime in Settings.
              </p>
            </>
          )}
        </div>

        <div className="modal-footer">
          {step > 1 && step < 4 && (
            <button type="button" className="btn-cancel" onClick={() => setStep(step - 1)} disabled={saving}>
              <i className="fas fa-arrow-left"></i> Back
            </button>
          )}
          {allowDismiss && step < 4 && (
            <button type="button" className="btn-cancel" onClick={onDismiss} disabled={saving}>
              Skip for now
            </button>
          )}
          {step === 1 ? (
            <button
              type="button"
              className="btn-primary-green"
              disabled={selectedCount === 0}
              onClick={() => setStep(2)}
            >
              Continue <i className="fas fa-arrow-right"></i>
            </button>
          ) : step === 2 ? (
            <button
              type="button"
              className="btn-primary-green"
              disabled={usesEiaCertificates === null}
              onClick={() => setStep(3)}
            >
              Continue <i className="fas fa-arrow-right"></i>
            </button>
          ) : step === 3 ? (
            <button
              type="button"
              className="btn-primary-green"
              disabled={saving || hasWorkersOnSite === null}
              onClick={handleSubmit}
            >
              {saving ? 'Saving...' : 'Finish Setup'}
            </button>
          ) : (
            <button type="button" className="btn-primary-green" onClick={() => finishAndGo(null)}>
              I'll do this later - go to Dashboard
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default OrgOnboardingModal;
